import React, { useState, useMemo, useCallback } from "react";
import { Form, Button, Typography, Grid } from "antd";
import { useSelector, useDispatch } from "react-redux";
import SingleSelect from "@/common/document-filters/single-select";
import { hidePanel } from "@/pages/shared/shared.reducer";
import {
  haveAbilityToChangeSites,
  allowedSiteListToChangeSites,
} from "@/pages/login/permission-service";
import { useFormContext } from "@/common/form-context";
import { dashboardApi } from "@/pages/dashboard/dashboard.reducer";
import { openErrorNotification } from "@/notification";

const { Paragraph } = Typography;

const ChangeSitesPanel = ({ selectedItems, currentSiteId, refetch }) => {
  const screens = Grid.useBreakpoint();
  const isMobile = screens.xs;
  const dispatch = useDispatch();
  const [siteId, setSiteId] = useState(null);

  const sites = useSelector((state) => state.dashboardPage.sites);
  const permissions = useSelector((state) => state.userDetails.permissions);

  const { form } = useFormContext();

  const exitPanel = () => {
    dispatch(hidePanel("changeSitesPanel"));
    form.resetFields();
    setSiteId(null);
  };

  const changeTextFilter = useCallback(
    (value) => {
      setSiteId(value);
      form.setFieldsValue({ siteId: value });
    },
    [form]
  );

  const handleSubmit = useCallback(() => {
    const itemsToUpdate = haveAbilityToChangeSites(permissions, selectedItems);
    if (itemsToUpdate && itemsToUpdate.length > 0) {
      dispatch(
        dashboardApi.endpoints.changeSite.initiate({
          siteId,
          documents: itemsToUpdate,
          panelId: "changeSitesPanel",
          refetch,
        })
      );
    } else {
      const errorMessage =
        !itemsToUpdate || itemsToUpdate.length === 0
          ? "No selected documents"
          : "Invalid fields";
      openErrorNotification(errorMessage);
    }
  }, [dispatch, permissions, refetch, selectedItems, siteId]);

  const items = useMemo(
    () => haveAbilityToChangeSites(permissions, selectedItems),
    [permissions, selectedItems]
  );

  const accountId = sites
    ? sites.find((item) => item.siteId === currentSiteId)?.accountId
    : null;
  const permissionSites = useMemo(
    () =>
      allowedSiteListToChangeSites(
        permissions,
        sites,
        currentSiteId,
        accountId
      ),
    [permissions, sites, currentSiteId, accountId]
  );
  const options = useMemo(() => {
    return permissionSites
      ? Object.entries(permissionSites).map(([key, value]) => ({
          key,
          label: value.siteFullName,
          value: value.siteId,
        }))
      : [];
  }, [permissionSites]);

  const documentParagraphs = useMemo(
    () =>
      items.map((item, index) => (
        <Paragraph
          key={index}
          ellipsis={{ rows: 5, expandable: true, symbol: "more" }}
        >
          <div>
            <span className="body-large-roman" style={{ color: "var(--dms-color-text-muted)" }}>
              ●&nbsp;
            </span>
            <span style={{ color: "var(--dms-color-text)" }}>
              <small>
                <b>{item.siteName}</b>/{item.category}/{item.subCategory}/
              </small>
              <b>{item.documentNumber}</b>
            </span>
          </div>
        </Paragraph>
      )),
    [items]
  );

  return (
    <>
      <Form form={form} onFinish={handleSubmit}>
        <Form.Item
          name="siteId"
          rules={[{ required: true, message: "Please choose new Site" }]}
          className="mt-3"
        >
          <SingleSelect
            title="Site"
            id="siteId"
            width="100%"
            initialSelect={options.find((item) => item.value === siteId)?.key}
            onSelect={(value) => changeTextFilter(value.value)}
            options={options}
          />
        </Form.Item>
        <div className="body-large-roman mt-3" style={{ color: "var(--dms-color-text-muted)" }}>
          Document / drawing #:
        </div>
        <div style={{ gap: 6, marginBottom: 26 }}>{documentParagraphs}</div>
      </Form>
      <div className="footer document-detail-footer" 
        style={ isMobile ? {
          display: "grid",
          rowGap: "10px",
          width: "100%",
          minHeight: "22vh",
          alignContent: "flex-start"
      } : {
          display: "flex",
          paddingRight: "45px"
      }}>
        {/* <Button disabled={!isAnyFieldTouched()} htmlType="submit" type="primary" onClick={() => {setNeedClose(false); form.submit()}} >
          <i className="ri-save-line pe-2"></i> Save
        </Button> */}
        <Button
          disabled={siteId === null}
          htmlType="submit"
          type="primary"
          onClick={() => form.submit()}
        >
          <i className="ri-save-line"></i> Save and Close
        </Button>
        <Button className="common-button" onClick={exitPanel}>
          <i className="ri-close-line" />
          Cancel
        </Button>
      </div>
    </>
  );
};

export default ChangeSitesPanel;
